/**
 * What the grid on a configuration page is given: the flag those pages stand behind, the height
 * the grid fills, and the columns, the keyboard and the draft row they share.
 *
 * Departments, Managers, Projects, Roles and Substitutes are the same page five times over.
 * What all five have in common lives here; what one of them alone does stays in its own
 * component, and the heading and card around the grid are in ConfigurationPage.component.
 *
 * The grid itself is AppGrid, and the classes the pieces below wear - .app-grid__actions, and
 * .app-grid__person on a cell that stacks a name over an email - are styled in its stylesheet.
 * Every page rendering one of these renders an AppGrid too, which is what loads that stylesheet.
 */
import {useEffect, useRef} from 'react';
import type {KeyboardEvent, MutableRefObject} from 'react';
import type {
    ColDef,
    GridApi,
    PostSortRowsParams,
    SuppressHeaderKeyboardEventParams,
    SuppressKeyboardEventParams
} from 'ag-grid-community';
import {PencilIcon, Trash2Icon} from 'lucide-react';
import {Button, ButtonType} from 'components/common/Button.component';
import {ICON_SIZE, KEY} from 'components/common/constants';
import {launchDarkly} from 'utils/launchDarkly';
import type {Translate} from 'types/myTasks';

// swaps the classic lists for the ag-grid renderings of the configuration pages
export const AG_GRID_IN_CONFIGURATION_FLAG = 'fe-use-ng-grid-in-configuration';

/**
 * Read at render by each page's own wrapper, so the page itself decides which of its two
 * renderings it is - rather than the route deciding for it.
 */
export const isAgGridInConfigurationEnabled = (): boolean =>
    launchDarkly.userClient?.variation(AG_GRID_IN_CONFIGURATION_FLAG, false) ?? false;

// one line of text, with room for the ghost buttons beside it
export const ROW_HEIGHT = 56;

// what a card keeps below its grid: the card's own padding and the page's bottom margin
export const CARD_BOTTOM_GAP = 48;

// the same, on a page that also carries an Add button (40px, and the 24px it stands off the grid)
export const ADD_BUTTON_BOTTOM_GAP = 112;

/**
 * The height options a configuration grid is measured with (see useAvailableHeight): the card is
 * the block above it that shows and hides, and the gap is what the page keeps below it.
 */
export const configurationGridHeight = (page: string, bottomGap: number = CARD_BOTTOM_GAP) =>
    Object.freeze({bottomGap, watchSelector: `.new-${page}__card`});

/* ------------------------------------------------------------------------------------------- *
 * Columns
 * ------------------------------------------------------------------------------------------- */

export const ACTIONS_COL = 'actions';

// wide enough for the two ghost buttons a row wears
export const ACTIONS_WIDTH = 110;

// wide enough for the Save and Cancel buttons a row in edit mode wears instead
export const EDITING_ACTIONS_WIDTH = 180;

/**
 * A column that names itself: a narrowed column clips its heading, and the tooltip is what spells
 * it out again. Everything else is the column's own - a header tooltip of its own included.
 */
export function gridColumn<TRow>(column: ColDef<TRow>): ColDef<TRow> {
    return {headerTooltip: column.headerName, ...column};
}

/**
 * The column holding a row's buttons: at the end of the row, at a width of its own since there is
 * nothing in it to read, and neither ordering the rows nor worth narrowing.
 */
export function actionsColumn<TRow>({width = ACTIONS_WIDTH, ...column}: ColDef<TRow>): ColDef<TRow> {
    return {
        colId: ACTIONS_COL,
        type: 'rightAligned',
        sortable: false,
        resizable: false,
        ...column,
        width,
        minWidth: width,
        maxWidth: width
    };
}

/** The Edit button of a row, so focus can go back to it once that row's editor closes. */
export const editButtonId = (page: string, rowId: string) => `${page}-edit-button-${rowId}`;

/** The Add button of a page, so focus can go back to it once a draft row is done with. */
export const addButtonId = (page: string) => `${page}-add-button`;

export interface RowActionsProps {
    /** The page, as its class names and ids spell it - see ConfigurationPageProps. */
    page: string;
    rowId: string;
    disabled?: boolean;
    onEdit: () => void;
    /** Left out on a page whose rows are not the user's to delete - departments, which come from the master data. */
    onDelete?: () => void;
    translate: Translate;
}

/** The Edit and Delete a row carries, which are the tab stops of the actions column. */
export const RowActions = ({page, rowId, disabled, onEdit, onDelete, translate}: RowActionsProps) => {
    const editLabel = translate('workflows.edit');
    const deleteLabel = translate('workflows.delete');

    return (
        <span className="app-grid__actions">
            <Button id={editButtonId(page, rowId)}
                    type={ButtonType.GHOST}
                    iconOnly={true}
                    icon={<PencilIcon size={ICON_SIZE.MEDIUM}/>}
                    disabled={disabled}
                    onClick={onEdit}
                    ariaLabel={editLabel}
                    tooltip={{id: `${page}-edit-tooltip-${rowId}`, content: () => editLabel}}
            />
            {onDelete &&
                <Button type={ButtonType.GHOST}
                        iconOnly={true}
                        icon={<Trash2Icon size={ICON_SIZE.MEDIUM}/>}
                        disabled={disabled}
                        onClick={onDelete}
                        ariaLabel={deleteLabel}
                        tooltip={{id: `${page}-delete-tooltip-${rowId}`, content: () => deleteLabel}}
                />
            }
        </span>
    );
};

/* ------------------------------------------------------------------------------------------- *
 * The keyboard
 * ------------------------------------------------------------------------------------------- */

// the tab order inside a grid is these, in the order the page draws them
export const SORTABLE_HEADERS = '.ag-header-cell-sortable';
// a disabled button is no stop, which is every button on a page a system viewer reads
export const ACTION_BUTTONS = '.app-grid__actions button:not(:disabled)';

// how many frames an element is given to be drawn before focus gives up on it (see focusWhenDrawn)
const FOCUS_RENDER_ATTEMPTS = 5;

/**
 * Focuses an element the grid may not have drawn yet: the rows are virtualised, so one that has
 * just been scrolled to is only in the document a frame or more later - and a cell leaving edit
 * mode still holds the editor's own controls until ag-grid has drawn it again.
 */
export function focusWhenDrawn(elementId: string, attempts = FOCUS_RENDER_ATTEMPTS): void {
    const element = document.getElementById(elementId);
    if (element) {
        element.focus();
    } else if (attempts > 0) {
        requestAnimationFrame(() => focusWhenDrawn(elementId, attempts - 1));
    }
}

/**
 * Takes Tab off the heading the tab order leaves the header row from - the last one that sorts,
 * and so the last one worth standing on. The grid would walk on to the headings that do nothing,
 * and then loop back to the first, which is a ring focus never gets out of; walkRowControls
 * carries it into the rows instead. Shift-Tab is left alone - walking back up the headings is
 * what it should do.
 */
export const tabOutOfTheHeaderIsNotTheGrids = ({event}: SuppressHeaderKeyboardEventParams) =>
    event.key === KEY.TAB && !event.shiftKey;

/**
 * Takes Tab off the grid in a cell that carries controls of its own.
 *
 * The grid answers Tab itself, by walking its cells - and with cell focus suppressed (see
 * AppGrid) it has no cell to walk to, so it takes focus out of the grid altogether. A button in
 * a cell would then be somewhere focus can land and never leave. Suppressed, the key is
 * walkRowControls' to answer instead.
 */
export const tabIsNotTheGrids = ({event}: SuppressKeyboardEventParams) => event.key === KEY.TAB;

/**
 * The same, and every other key besides while the row is being edited - so an editor keeps its
 * caret and its list rather than having Enter, Escape and the arrows read as navigation.
 */
export const cellOwnsKeysWhileEditing = <TRow extends { id: string }, >(editingRowId: string | null) =>
    (params: SuppressKeyboardEventParams<TRow>) =>
        params.event.key === KEY.TAB || params.data?.id === editingRowId;

/**
 * What a page does with Tab once it walks off the end of the controls the grid has drawn, given
 * the control it left and the direction it was going. True where it was answered, which is what
 * keeps the key off the browser.
 */
export type OffTheEndOfTheRows = (target: HTMLElement, step: number) => boolean;

/**
 * Walks Tab through the controls the rows carry - the buttons of a row, the editors of the row
 * being edited - picking up where the headings leave off.
 *
 * What the grid and the browser would do between them is walk every heading, the ones that do
 * nothing included, in a ring, and stop on each row before the controls it carries: the row is a
 * tab stop of the grid's own making (see AppGrid), and standing on one does nothing. Every step
 * below is one of those skipped. The last sortable heading hands focus to the first control, and
 * a control hands it to the one beside it. Off the last control of the last drawn row the key is
 * left to the browser - which carries focus out of the grid, where it should go - unless the page
 * has something further of its own (see OffTheEndOfTheRows).
 */
export function walkRowControls(event: KeyboardEvent<HTMLDivElement>,
                                controlsSelector: string,
                                offTheEnd?: OffTheEndOfTheRows): void {
    if (event.key !== KEY.TAB) {
        return;
    }

    const grid = event.currentTarget;
    const controls = Array.from(grid.querySelectorAll<HTMLElement>(controlsSelector));
    if (controls.length === 0) {
        return;
    }

    const headings = Array.from(grid.querySelectorAll<HTMLElement>(SORTABLE_HEADERS));
    const lastHeading = headings[headings.length - 1];
    const target = event.target as HTMLElement;

    const moveTo = (element: HTMLElement | undefined) => {
        if (!element) {
            return;
        }
        element.focus();
        event.preventDefault();
    };

    if (target === lastHeading && !event.shiftKey) {
        moveTo(controls[0]);
        return;
    }

    const drawn = controls.indexOf(target);
    if (drawn === -1) {
        return;
    }

    const step = event.shiftKey ? -1 : 1;

    // among the rows the grid has drawn, the next control is simply the one beside this
    const neighbour = controls[drawn + step];
    if (neighbour) {
        moveTo(neighbour);
        return;
    }

    // off the end of what is drawn, which on a virtualised list is rarely the end of the list
    if (offTheEnd?.(target, step)) {
        event.preventDefault();
        return;
    }

    // nothing further that way: back up into the headings, or on out of the grid altogether
    if (event.shiftKey) {
        moveTo(lastHeading);
        return;
    }
    leaveGrid(event, grid);
}

/**
 * Takes Tab off the last control the grid has to offer, and out of the grid.
 *
 * The key cannot be left to the browser. ag-grid stands a tab guard at either end of the grid and
 * makes a tab stop of every row (see AppGrid, which has it draw rows rather than cells for the
 * keyboard), so what the browser reaches next is the guard - which hands focus to the row under
 * the one just left, and to the row under that on the Tab after it. A row is nothing to stand on
 * here: the tab order is the controls the rows carry, and the only one a row shows while another
 * is being edited is an Edit button that is disabled. Answering the key ourselves is what keeps
 * the guards out of it, since they wait on focus rather than on the key.
 *
 * Where the page has something after the grid - the Add button under it - that is where focus
 * goes. Where it has nothing, it goes to the page itself, which is both out of the grid and the
 * place the tab order carries on from: the next Tab starts the page again from the top, rather
 * than from the row it would otherwise have left off at.
 */
function leaveGrid(event: KeyboardEvent<HTMLDivElement>, grid: HTMLElement): void {
    const next = nextStopAfter(grid);
    if (next) {
        next.focus();
    } else {
        // focus has to land somewhere for the order to carry on from it - blurring alone leaves
        // the browser counting on from where it stood, which is back among the rows
        document.body.tabIndex = -1;
        document.body.focus();
    }
    event.preventDefault();
}

/**
 * What the document's own tab order holds, less the things it passes over: a disabled control is
 * no stop, and neither is anything taken out of the order with a negative index.
 */
const TAB_STOPS = [
    'a[href]',
    'button:not(:disabled)',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex^="-"])'
].join(', ');

/**
 * The first thing the tab order reaches past the grid, which is where Tab off the last control
 * of the last row belongs.
 *
 * Leaving that key to the browser would not get there: ag-grid makes every row a tab stop of its
 * own (see AppGrid, which has it draw rows rather than cells for the keyboard), so the browser
 * would walk down the rows one at a time - each of them standing on nothing the user can act on,
 * the disabled Edit button of a row being edited elsewhere included.
 */
function nextStopAfter(grid: HTMLElement): HTMLElement | undefined {
    return Array.from(document.querySelectorAll<HTMLElement>(TAB_STOPS)).find(stop =>
        !grid.contains(stop)
        && !!(grid.compareDocumentPosition(stop) & Node.DOCUMENT_POSITION_FOLLOWING));
}

/**
 * Hands keyboard focus back once an editor closes: to the Edit button of the row it closed on,
 * which is where the tab order stood when that row was opened - or to the Add button, when the
 * row that closed was the draft and so is no longer there to focus.
 *
 * Returns the ref the page writes the row being left to, as it leaves edit mode: Save and Cancel
 * unmount themselves as they are pressed, which would otherwise drop focus onto the document body.
 */
export function useReturnFocusAfterEdit<TRow>(page: string,
                                              editingRowId: string | null,
                                              rowData: unknown,
                                              api: MutableRefObject<GridApi<TRow> | null>): MutableRefObject<string | null> {

    const returnFocusTo = useRef<string | null>(null);

    useEffect(() => {
        const rowId = returnFocusTo.current;
        if (editingRowId !== null || rowId === null) {
            return;
        }
        returnFocusTo.current = null;

        if (!api.current?.getRowNode(rowId)) {
            document.getElementById(addButtonId(page))?.focus();
            return;
        }
        focusWhenDrawn(editButtonId(page, rowId));
    }, [page, editingRowId, rowData, api]);

    return returnFocusTo;
}

/* ------------------------------------------------------------------------------------------- *
 * The draft row
 * ------------------------------------------------------------------------------------------- */

/**
 * Keeps the draft row the Add button opens under the saved ones, wherever the sort would
 * otherwise put it: its cells are empty, so any column would carry it to whichever end the empty
 * value belongs at.
 *
 * A page holds on to what this hands back rather than calling it as it renders: ag-grid reads a
 * new postSortRows as a reason to sort the rows again, which redraws them - and a row redrawn
 * from under the keyboard takes focus with it.
 */
export const keepDraftLast = (draftRowId: string) =>
    <TRow extends { id: string }>({nodes}: PostSortRowsParams<TRow>) => {
        const draft = nodes.findIndex(node => node.data?.id === draftRowId);
        if (draft !== -1) {
            nodes.push(...nodes.splice(draft, 1));
        }
    };
